import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { useTranslation } from "react-i18next"
import { CapabilitiesResponseDataSchema } from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilitiesDictionary } from "../hooks/useCapabilitiesDictionary"
import { useUninstallCapabilityMutation } from "../hooks/useUninstallCapabilityMutation"

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  onOpenChange: (open: boolean) => void
}

export function UninstallCapabilityDialog({ capability, onOpenChange }: Props) {
  const { t } = useTranslation()
  const { mutateAsync: uninstallCapability, isPending } = useUninstallCapabilityMutation()

  const capabilitiesDictionary = useCapabilitiesDictionary()
  const { name } =
    capabilitiesDictionary[capability.identifier as keyof typeof capabilitiesDictionary]

  async function onSubmit() {
    await uninstallCapability({ id: capability.id })

    toast.success(t("capabilities.list.uninstall.success", { name }))

    onOpenChange(false)
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("capabilities.list.uninstall.dialog.title", { name })}</DialogTitle>
          <DialogDescription>
            {t("capabilities.list.uninstall.dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={onSubmit} disabled={isPending}>
            {t("shared.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
